import {HttpClient, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IdObject } from 'src/app/core/id-object';
import { environment } from 'src/environments/environment';
import { Attribute, AttributeDraft, AttributeSearchData } from './attribute';
import {Deposit, DepositSearchData} from "../deposit/deposit";
import {PagedResponse, Paging, PagingHeader} from "../../core/paging/paging";

@Injectable({
  providedIn: 'root',
})
export class AttributeService {
  private static listUrl = (apiUrl: string): string => `${apiUrl}attribute`;
  private static singleUrl = (apiUrl: string, id: string): string => `${apiUrl}attribute/${id}`;
  private static searchUrl = (apiUrl: string): string => `${apiUrl}attribute/search`;

  constructor(private httpClient: HttpClient, private logger: NGXLogger) {
  }

  public getList(): Observable<Attribute[]> {
    const {apiUrl} = environment;
    const url = AttributeService.listUrl(apiUrl);

    return this.httpClient
      .get<Attribute[]>(url)
      .pipe(catchError(this.handleError<Attribute[]>('AttributeService->getList', [])));
  }

  public getSingle(id: string): Observable<Attribute> {
    const {apiUrl} = environment;
    const url = AttributeService.singleUrl(apiUrl, id);

    return this.httpClient
      .get<Attribute>(url)
      .pipe(catchError(this.handleError<Attribute>('AttributeService->getSingle')));
  }

  public create(attributeDraft: AttributeDraft): Observable<string> {
    const {apiUrl} = environment;
    const url = AttributeService.listUrl(apiUrl);
    const headers = new HttpHeaders({'Content-Type': 'application/json'});

    return this.httpClient.post<IdObject>(url, attributeDraft, {headers}).pipe(
      map((idObject: IdObject) => idObject.id),
      catchError(this.handleError<string>('AttributeService->create')),
    );
  }

  public patch(id: string, attributeDraft: AttributeDraft): Observable<boolean> {
    const {apiUrl} = environment;
    const url = AttributeService.singleUrl(apiUrl, id);
    const headers = new HttpHeaders({'Content-Type': 'application/json'});

    return this.httpClient.patch<void>(url, attributeDraft, {headers}).pipe(
      map(() => true),
      catchError(this.handleError<boolean>('AttributeService->patch', false)),
    );
  }

  public remove(id: string): Observable<boolean> {
    const {apiUrl} = environment;
    const url = AttributeService.singleUrl(apiUrl, id);

    return this.httpClient.delete<void>(url).pipe(
      map(() => true),
      catchError(this.handleError('AttributeService->remove', false)),
    );
  }

  public search(
    attributeSearchData: AttributeSearchData,
    paging?: Paging,
  ): Promise<PagedResponse<Attribute[]>> {
    const {apiUrl} = environment;
    const url = AttributeService.searchUrl(apiUrl);
    const headers = new HttpHeaders({'Content-Type': 'application/json'});
    const params = paging ? new HttpParams()
      .set('page', paging.currentPage)
      .set('sort', paging.sort)
      .set('sortDirection', paging.sortDirection)
      .set('perPage', paging.perPage) : undefined;

    return new Promise(async resolve => {
      this.httpClient
        .post<Attribute[]>(
          url,
          attributeSearchData,
          {params, headers, observe: 'response'},
        )
        .pipe(catchError(this.handleError<HttpResponse<Attribute[]>>(
          'AttributeService->search',
          undefined,
        )))
        .subscribe((r) => {
          console.log('DONE - Response:', r.body);
          if (r.body) {
            const pagingHeader: PagingHeader = this.getPaging(r.headers);
            resolve({pagingHeader, items: r.body});
          }
        });
    });
  }

  getPaging(headers: HttpHeaders): PagingHeader {
    if (headers && headers.has('pagination-countavailable')) {
      const totalAttributes = headers.get('pagination-count');
      const perPage = headers.get('pagination-limit');
      const currentPage = headers.get('pagination-page');
      const hasNextPage = headers.get('pagination-nextpage');

      if (totalAttributes && perPage && currentPage && hasNextPage) {
        return {
          totalItems: parseInt(totalAttributes),
          perPage: parseInt(perPage),
          currentPage: parseInt(currentPage),
          hasNextPage: hasNextPage === 'true',
        };
      }
    }

    return {
      totalItems: 0, perPage: -1, currentPage: 0, hasNextPage: true,
    };
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(
    operation = 'operation',
    result?: T,
  ): (error: any) => Observable<T> {
    return (error: any): Observable<T> => {
      this.logger.error(`${operation} failed: `, error);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}
